import { type Editor } from "@tiptap/react";
import { useState, useEffect, useCallback } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import {
  Bold,
  Italic,
  Underline,
  List,
  ListOrdered,
  Link as LinkIcon,
  Smile,
  ExternalLink,
  Pencil,
  Trash2,
} from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Separator } from "@/components/ui/separator";
import { CONSTRUCTION_EMOJIS, COMMON_EMOJIS } from "@/constants";

interface HoveredLinkInfo {
  href: string;
  text: string;
  top: number;
  left: number;
}

const FormSchema = z.object({
  text: z.string().min(1, "Link text is required"),
  url: z
    .string()
    .min(1, "URL is required")
    .refine((val) => {
      // Allow URLs with http://, https://, or www.
      return (
        /^(https?:\/\/|www\.)/.test(val) ||
        /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/.test(val)
      );
    }, "Please enter a valid URL"),
});

// Helper to normalize URL (add https:// if missing)
const normalizeUrl = (url: string): string => {
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  if (url.startsWith("www.")) {
    return `https://${url}`;
  }
  // If it looks like a domain, add https://
  if (/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+/.test(url)) {
    return `https://${url}`;
  }
  return url;
};

const Toolbar: React.FunctionComponent<{
  editor: Editor | null;
  enableLink?: boolean;
  enableEmoji?: boolean;
}> = ({ editor, enableLink = false, enableEmoji = false }) => {
  const { t } = useTranslation();
  // Shared emoji sets (same source the community composer uses) so labels are translated
  // and the list lives in exactly one place.
  const constructionEmojis = CONSTRUCTION_EMOJIS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));
  const commonEmojis = COMMON_EMOJIS.map((item) => ({
    emoji: item.emoji,
    label: t(item.labelKey),
  }));
  const [isLinkModelOpen, setIsLinkModelOpen] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [hoveredLink, setHoveredLink] = useState<HoveredLinkInfo | null>(null);
  const [isHoveringPopup, setIsHoveringPopup] = useState(false);

  const form = useForm<z.infer<typeof FormSchema>>({
    defaultValues: {
      text: "",
      url: "",
    },
    resolver: zodResolver(FormSchema),
  });

  // Get selected text or link text when opening the dialog
  const openLinkDialog = () => {
    if (!editor) return;
    const { from, to } = editor.state.selection;
    const selectedText = editor.state.doc.textBetween(from, to, " ");
    const existingHref = editor.getAttributes("link").href || "";

    form.setValue("text", selectedText || "");
    form.setValue("url", existingHref);
    setIsLinkModelOpen(true);
  };

  // Handle link hover events
  const handleLinkMouseEnter = useCallback((event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (target.tagName === "A" && target.hasAttribute("href")) {
      const rect = target.getBoundingClientRect();

      setHoveredLink({
        href: target.getAttribute("href") || "",
        text: target.textContent || "",
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, []);

  const handleLinkMouseLeave = useCallback(() => {
    // Delay hiding to allow moving to popup
    setTimeout(() => {
      if (!isHoveringPopup) {
        setHoveredLink(null);
      }
    }, 100);
  }, [isHoveringPopup]);

  // Set up link hover listeners
  useEffect(() => {
    if (!editor || !enableLink) return;

    const editorElement = editor.view.dom;

    const handleMouseOver = (e: Event) => handleLinkMouseEnter(e as MouseEvent);
    const handleMouseOut = (e: Event) => {
      const relatedTarget = (e as MouseEvent).relatedTarget as HTMLElement;
      if (!relatedTarget?.closest("a")) {
        handleLinkMouseLeave();
      }
    };

    editorElement.addEventListener("mouseover", handleMouseOver);
    editorElement.addEventListener("mouseout", handleMouseOut);

    return () => {
      editorElement.removeEventListener("mouseover", handleMouseOver);
      editorElement.removeEventListener("mouseout", handleMouseOut);
    };
  }, [editor, enableLink, handleLinkMouseEnter, handleLinkMouseLeave]);

  // Edit link from hover popup
  const handleEditFromHover = () => {
    if (!editor || !hoveredLink) return;

    form.setValue("text", hoveredLink.text);
    form.setValue("url", hoveredLink.href);
    setHoveredLink(null);
    setIsLinkModelOpen(true);
  };

  // Remove link from hover popup
  const handleRemoveFromHover = () => {
    if (!editor) return;

    // Find and select the link, then remove it
    const { state } = editor;
    let linkPos: { from: number; to: number } | null = null;

    state.doc.descendants((node, pos) => {
      if (
        node.isText &&
        node.marks.some(
          (mark) => mark.type.name === "link" && mark.attrs.href === hoveredLink?.href,
        )
      ) {
        linkPos = { from: pos, to: pos + node.nodeSize };
        return false;
      }
    });

    if (linkPos) {
      editor.chain().focus().setTextSelection(linkPos).unsetLink().run();
    }

    setHoveredLink(null);
  };

  if (!editor) {
    return null;
  }

  const addEmoji = (emoji: string) => {
    editor.commands.insertContent(emoji);
    editor.commands.focus();
    setShowEmojiPicker(false);
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    if (event) {
      if (typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
    }

    return form.handleSubmit(async (values: z.infer<typeof FormSchema>) => {
      const { text, url: rawUrl } = values;

      if (!rawUrl || !text) {
        return;
      }

      // Normalize URL (add https:// if needed)
      const url = normalizeUrl(rawUrl);

      const { from, to } = editor.state.selection;
      const hasSelection = from !== to;

      // Create link content with a trailing space as separate nodes
      const linkContent = [
        {
          type: "text",
          marks: [{ type: "link", attrs: { href: url, target: "_blank" } }],
          text: text,
        },
        {
          type: "text",
          text: " ",
        },
      ];

      if (hasSelection) {
        // Replace selected text with link
        editor.chain().focus().deleteSelection().insertContent(linkContent).run();
      } else if (editor.isActive("link")) {
        // Update existing link
        editor
          .chain()
          .focus()
          .extendMarkRange("link")
          .deleteSelection()
          .insertContent(linkContent)
          .run();
      } else {
        // Insert new link at cursor position
        editor.chain().focus().insertContent(linkContent).run();
      }

      setIsLinkModelOpen(false);
      form.reset();
    })(event);
  };

  return (
    <div className="border-input bg-background flex w-full flex-wrap items-center justify-between gap-2 rounded-b-lg border-t p-1.5 text-sm font-medium">
      <div className="flex w-full flex-wrap items-center justify-start gap-2 lg:w-10/12">
        {/* ------ BOLD ------ */}
        <Button
          onClick={(e) => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run()}
          type="button"
          size="icon"
          variant="ghost"
          className={cn(
            editor.isActive("bold")
              ? "bg-brand-accent text-primary"
              : "text-muted-foreground hover:text-primary bg-transparent hover:bg-blue-100",
            "h-8 w-8",
          )}
        >
          <Bold className="h-4 w-4" />
        </Button>
        {/* ------ ITALIC ------ */}
        <Button
          onClick={(e) => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run()}
          type="button"
          size="icon"
          variant="ghost"
          className={cn(
            editor.isActive("italic")
              ? "bg-brand-accent text-primary"
              : "text-muted-foreground hover:text-primary bg-transparent hover:bg-blue-100",
            "h-8 w-8",
          )}
        >
          <Italic className="h-4 w-4" />
        </Button>
        {/* ------ UNDERLINE ------ */}
        <Button
          onClick={(e) => editor.chain().focus().toggleUnderline().run()}
          type="button"
          size="icon"
          variant="ghost"
          className={cn(
            editor.isActive("underline")
              ? "bg-brand-accent text-primary"
              : "text-muted-foreground hover:text-primary bg-transparent hover:bg-blue-100",
            "h-8 w-8",
          )}
        >
          <Underline className="h-4 w-4" />
        </Button>
        {/* ------ LINK ------ */}
        {enableLink && (
          <>
            <Button
              size="icon"
              variant="ghost"
              className={cn(
                editor.isActive("link")
                  ? "bg-brand-accent text-primary"
                  : "text-muted-foreground hover:text-primary bg-transparent hover:bg-blue-100",
                "h-8 w-8",
              )}
              type="button"
              onClick={openLinkDialog}
            >
              <LinkIcon className="h-4 w-4" />
            </Button>
            <Dialog
              open={isLinkModelOpen}
              onOpenChange={(value) => {
                setIsLinkModelOpen(value);
                if (!value) form.reset();
              }}
            >
              <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                  <DialogTitle className="text-lg font-semibold tracking-tight">
                    {t("editor.add_link")}
                  </DialogTitle>
                  <DialogDescription>{t("editor.add_link_desc")}</DialogDescription>
                </DialogHeader>
                <Form {...form}>
                  <form className="space-y-4" onSubmit={onSubmit}>
                    <FormField
                      name="text"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("editor.link_text")}</FormLabel>
                          <FormControl>
                            <Input
                              type="text"
                              placeholder={t("editor.link_text_placeholder")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      name="url"
                      control={form.control}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("editor.url")}</FormLabel>
                          <FormControl>
                            <Input type="url" placeholder="https://example.com" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <DialogFooter>
                      <div className="flex flex-wrap gap-3">
                        <Button
                          variant="outline"
                          type="button"
                          onClick={() => {
                            setIsLinkModelOpen(false);
                            editor.commands.blur();
                            form.reset();
                          }}
                        >
                          {t("editor.cancel")}
                        </Button>
                        <Button type="submit">{t("editor.save")}</Button>
                      </div>
                    </DialogFooter>
                  </form>
                </Form>
              </DialogContent>
            </Dialog>
          </>
        )}
        {/* ------ BULLET LIST------ */}
        <Button
          onClick={(e) => editor.chain().focus().toggleBulletList().run()}
          type="button"
          size="icon"
          variant="ghost"
          className={cn(
            editor.isActive("bulletList") && editor.getAttributes("link").href !== null
              ? "bg-brand-accent text-primary"
              : "text-muted-foreground hover:text-primary bg-transparent hover:bg-blue-100",
            "h-8 w-8",
          )}
        >
          <List className="h-4 w-4" />
        </Button>
        {/* ------ ORDERED LIST ------ */}
        <Button
          onClick={(e) => editor.chain().focus().toggleOrderedList().run()}
          type="button"
          size="icon"
          variant="ghost"
          className={cn(
            editor.isActive("orderedList")
              ? "bg-brand-accent text-primary"
              : "text-muted-foreground hover:text-primary bg-transparent hover:bg-blue-100",
            "h-8 w-8",
          )}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        {/* ------ EMOJI PICKER ------ */}
        {enableEmoji && (
          <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
            <PopoverTrigger asChild>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className={cn(
                  showEmojiPicker
                    ? "bg-brand-accent text-primary"
                    : "text-muted-foreground hover:text-primary bg-transparent hover:bg-blue-100",
                  "h-8 w-8",
                )}
              >
                <Smile className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80 rounded-xl p-3 shadow-lg" align="start">
              <TooltipProvider>
                <div className="space-y-4">
                  <div>
                    <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                      🏗️ {t("editor.construction_emojis")}
                    </h4>
                    <div className="grid grid-cols-8 gap-1">
                      {constructionEmojis.map((item, index) => (
                        <Tooltip key={index}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => addEmoji(item.emoji)}
                              className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                              type="button"
                            >
                              {item.emoji}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{item.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="text-muted-foreground mb-2 text-xs font-semibold tracking-wide uppercase">
                      😊 {t("editor.common_emojis")}
                    </h4>
                    <div className="grid grid-cols-8 gap-1">
                      {commonEmojis.map((item, index) => (
                        <Tooltip key={index}>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => addEmoji(item.emoji)}
                              className="hover:bg-accent flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-transform hover:scale-110 active:scale-95"
                              type="button"
                            >
                              {item.emoji}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{item.label}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                </div>
              </TooltipProvider>
            </PopoverContent>
          </Popover>
        )}
      </div>
      {/* Link Hover Popup */}
      {enableLink && hoveredLink && (
        <div
          className="bg-popover border-border fixed z-50 flex items-center gap-2 rounded-xl border p-2 shadow-lg"
          style={{
            top: hoveredLink.top,
            left: hoveredLink.left,
          }}
          onMouseEnter={() => setIsHoveringPopup(true)}
          onMouseLeave={() => {
            setIsHoveringPopup(false);
            setHoveredLink(null);
          }}
        >
          <a
            href={hoveredLink.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary flex max-w-[200px] items-center gap-1 truncate text-sm hover:underline"
          >
            <ExternalLink className="h-3 w-3 shrink-0" />
            {hoveredLink.href}
          </a>
          <Separator orientation="vertical" className="h-4" />
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={handleEditFromHover}
            title="Edit link"
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-destructive hover:text-destructive h-7 w-7"
            onClick={handleRemoveFromHover}
            title="Remove link"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
};

export default Toolbar;
